import React, { ChangeEvent, useEffect, useRef, useState } from 'react'
import * as yup from 'yup'
import { debounce } from 'lodash'
import {
  StyledField,
  StyledLoadingPlaceholder,
  ErrorIcon,
} from './WorkspaceProfileURL.styles'
import { PROFILE_URL_PREFIX } from '@sketch/constants'
import { FormikErrors, FormikProps, FormikTouched } from 'formik'
import {
  useProfileShortURL,
  CurrentStatus,
} from '../../hooks/useProfileShortURL'
import { Input } from '@sketch/components'

import { ReactComponent as Checkmark } from '@sketch/icons/check-20'

type IconPerStatus = Record<CurrentStatus, React.ReactElement | undefined>

const DEBOUNCE_TIME = 1000

const ICON_PER_STATUS: IconPerStatus = {
  LOADING: <StyledLoadingPlaceholder data-testid="loading-icon" size="17px" />,
  ERROR: <ErrorIcon data-testid="error-icon" width="20px" height="20px" />,
  UNAVAILABLE: (
    <ErrorIcon data-testid="error-icon" width="20px" height="20px" />
  ),
  SUCCESS: <Checkmark data-testid="success-icon" width="20px" height="20px" />,
  EMPTY: undefined, // When we want to hide / clear icons on the input
}

const getRightIcon = (status: CurrentStatus) => ICON_PER_STATUS[status]

// This pattern should only allow kebab case string.
// It also cannot start or end with a dash and
// does not allow double dashses "invalid--short-url"
const PATTERN = /^(?:[a-z0-9]+(?:-[a-z0-9]+)*)$/gm

const URL_UNAVAILABLE = `This URL isn’t available.`

// Dynamic validation schema
export const getValidationSchema = (fieldName: string) => {
  return yup.object().shape({
    [fieldName]: yup
      .string()
      .min(2, 'Enter 2 or more characters')
      .max(255, 'Enter up to 255 characters')
      .matches(PATTERN, 'Use only letters, numbers, or dashes')
      .trim(),
  })
}

// Updated validateShortUrl to use dynamic schema
const validateShortUrl = (value: string, fieldName: string) => {
  const schema = getValidationSchema(fieldName)
  const isValid = schema.isValidSync({ [fieldName]: value })
  return isValid
}

// Updated getErrorMessage to use dynamic field name
const getErrorMessage = <
  TName extends string,
  TFormValues extends Record<TName, string>,
>({
  name,
  touched,
  errors,
  isUrlAvailable,
}: {
  name: TName
  touched: FormikTouched<TFormValues>
  errors: FormikErrors<TFormValues>
  isUrlAvailable: boolean
}) => {
  if (touched[name]) {
    if (!isUrlAvailable) {
      return URL_UNAVAILABLE
    }
    return errors[name] as string | undefined
  }
  return undefined
}

export interface WorkspaceProfileUrlFieldInputProps<
  TName extends string,
  TFormValues extends Record<TName, string>,
> {
  label: React.ReactNode
  name: TName
  formikBag: FormikProps<TFormValues>
  onChange?: (value: string) => void
  /** Triggered when the loading state of checkWorkspaceProfileShortUrl changes */
  onMutationLoadingChange?: (value: boolean) => void
  profileShortURLProps: ReturnType<typeof useProfileShortURL>
}

export const WorkspaceProfileUrlFieldInput = <
  TName extends string,
  TFormValues extends Record<TName, string>,
>({
  label,
  name,
  formikBag,
  onChange,
  onMutationLoadingChange,
  profileShortURLProps,
}: WorkspaceProfileUrlFieldInputProps<TName, TFormValues>) => {
  const { values, touched, errors } = formikBag

  const currentFieldValue = values[name] || ''

  const inputRef = useRef<HTMLInputElement>(null)
  const [newShortUrl, setNewShortUrl] = useState(currentFieldValue)
  const [onChangeFnTriggered, setOnChangeFnTriggered] = useState(false)

  const {
    // Mutations
    checkWorkspaceProfileShortUrl,
    // States
    currentShortUrl,
    setHasNewUrl,
    currentStatus,
    setCurrentStatus,
    // Booleans
    isUrlAvailable,
    isCheckingURL,
  } = profileShortURLProps

  // Only trigger onChange when the newShortUrl is successfully checked
  // and is available
  useEffect(() => {
    if (currentStatus === 'SUCCESS' && onChange && !onChangeFnTriggered) {
      onChange(newShortUrl)
      setOnChangeFnTriggered(true)
    }
  }, [currentStatus, onChange, newShortUrl, onChangeFnTriggered])

  useEffect(() => {
    onMutationLoadingChange?.(isCheckingURL)
  }, [isCheckingURL, onMutationLoadingChange])

  const handleGetURLAvailability = useRef(
    debounce(async (newShortUrl: string) => {
      await checkWorkspaceProfileShortUrl({
        variables: {
          shortUrlName: newShortUrl,
        },
      })
    }, DEBOUNCE_TIME)
  ).current

  const handleChange = async (
    event: ChangeEvent<HTMLInputElement>,
    actions: FormikProps<TFormValues>
  ) => {
    const { setFieldTouched, setFieldValue } = actions

    const cursorStart = event.target.selectionStart
    const cursorEnd = event.target.selectionEnd

    // Clear on change trigger flag
    setOnChangeFnTriggered(false)

    // Clear any debounce timers that might be running
    handleGetURLAvailability.cancel()

    // Replace spaces with dashes and convert to lowercase
    const newURL = event.target.value.replace(' ', '-').toLowerCase()

    // Reveals a new label
    setHasNewUrl(newURL !== currentShortUrl)

    setFieldTouched(name, true)
    setFieldValue(name, newURL)

    if (newURL.length === 0 || newURL === currentShortUrl) {
      // Hide icons if input is empty
      setCurrentStatus('EMPTY')
      return
    }

    if (!validateShortUrl(newURL, name)) {
      setCurrentStatus('ERROR')
      return
    }

    if (currentStatus !== 'LOADING') {
      setCurrentStatus('LOADING')
    }

    // Set cursor position to avoid the cursor
    // from jumping to the end of the input
    if (inputRef.current) {
      const input = inputRef.current

      setTimeout(() => {
        input.setSelectionRange(cursorStart, cursorEnd)
      })
    }

    await handleGetURLAvailability(newURL)
    setNewShortUrl(newURL)
  }

  const errorMessage = getErrorMessage({
    name,
    touched,
    errors,
    isUrlAvailable,
  })

  const { setFieldError } = formikBag

  useEffect(() => {
    // Only update if error actually changed
    if (!isUrlAvailable) {
      setFieldError(name, errorMessage)
    } else {
      setFieldError(name, undefined)
    }
  }, [name, setFieldError, errorMessage, isUrlAvailable])

  return (
    <StyledField name={name} hasError={!!errorMessage} label={label}>
      <Input
        ref={inputRef}
        type="text"
        name={name}
        prefix={PROFILE_URL_PREFIX}
        value={values[name]}
        onChange={event => handleChange(event, formikBag)}
        rightIcon={getRightIcon(currentStatus)}
        autoComplete="off"
      />
    </StyledField>
  )
}
